import { useState } from "react";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import { formatEther, isAddress, isAddressEqual } from "viem";
import { useAccount } from "wagmi";
import { ContainerShape, InnerShape, InnerShape2 } from "./components/Shape";
import { icons } from "./assets/icons/icons";
import Loader from "~/core/components/Loader";
import { API_URL } from "~/core/constants";
import { useToast } from "~/core/contexts/ToastContext";
import { api, isBlinkoLoginTokenValid } from "./api";
import { ResetLeaderboardModal } from "./modals/reset.leaderboard.modal";
import { useSetAtom } from "jotai";
import { atomWalletModal } from "./modals/wallet.modal";

// --- Types ---
interface KolProfile {
  address: string;
  name: string;
  picture: string | null;
  twitterId: string | null;
  twitterUsername: string | null;
  twitterHandle: string | null;
  harvestedHoney: string;
  referralHoney: string;
  totalHoney: string;
  profitShareBps: number;
  profitShareUntil: number;
  profitShareEarnings: string;
  settledProfitShare: string;
  pendingProfitShare: string;
  referralCode: string;
  referredByAddress: string | null;
  usedReferralCode: string | null;
  restrictedUntil: number | null;
  createdAt: number;
  stats: {
    referralsCount: number;
    totalGamesPlayed: number;
    highestMultiplier: number;
    bonusGamesReached: number;
    highestBonusLevel: number;
    currentDailyStreak: number;
    maxDailyStreak: number;
    todayHoney: string;
    todayReferralHoney: string;
    todayTotalHoney: string;
    dailyRankings: Record<string, number>;
  };
}

interface ApiResponse {
  success: boolean;
  data: KolProfile;
}

const fetchKolProfile = async (address: string): Promise<KolProfile | null> => {
  try {
    const response = await fetch(`${API_URL}/blinko/profiles/${address}`);
    const result: ApiResponse = await response.json();
    if (result.success) {
      return result.data;
    }
    return null;
  } catch (e) {
    console.error("Failed to fetch KOL profile:", e);
    return null;
  }
};

const useKolProfile = (address: string) => {
  return useSWR(["kol-profile", address], () => fetchKolProfile(address), {
    refreshInterval: 30000,
  });
};

// --- Components ---
const StatCard = ({
  title,
  value,
  subValue,
  icon,
  colorClass = "text-white",
}: {
  title: string;
  value: string | number | React.ReactNode;
  subValue?: string;
  icon: string;
  colorClass?: string;
}) => {
  return (
    <InnerShape2 cornerDiagonal={10} cornerRadius={8} strokeWidth={1}>
      <div className="flex flex-col gap-2 p-3 h-full justify-between">
        <div className="flex items-center gap-2">
          <InnerShape cornerDiagonal={6} cornerRadius={4} strokeWidth={1}>
            <div className="p-1.5">
              <img src={icon} className="w-3.5 h-3.5" alt={title} />
            </div>
          </InnerShape>
          <p className="text-[10px] uppercase font-blinko text-white/50 tracking-wider">
            {title}
          </p>
        </div>
        <div>
          <div
            className={`text-xl md:text-2xl font-black ${colorClass} truncate`}
          >
            {value}
          </div>
          {subValue && (
            <p className="text-xs font-bold text-white/50">{subValue}</p>
          )}
        </div>
      </div>
    </InnerShape2>
  );
};

const formatHoney = (honey: string) => {
  const num = parseInt(honey, 10);
  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(2)}M`;
  }
  if (num >= 1_000) {
    return `${(num / 1_000).toFixed(1)}K`;
  }
  return num.toLocaleString();
};

const formatEth = (val: bigint) => {
  const formatted = formatEther(val);
  const [int, dec] = formatted.split(".");
  if (!dec) return int;
  return `${int}.${dec.slice(0, 4)}`;
};

const formatSeasonStart = (seconds: number) =>
  new Date(seconds * 1000).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

const LeaderboardResetSection = ({
  ownerAddress,
  referralCode,
}: {
  ownerAddress: `0x${string}`;
  referralCode: string;
}) => {
  const toast = useToast();
  const [showConfirm, setShowConfirm] = useState(false);
  const setWalletModalVisible = useSetAtom(atomWalletModal.visible);

  const { data: sinceReset, mutate: mutateSinceReset } = useSWR(
    referralCode ? ["clan-leaderboard-since-reset", referralCode] : null,
    () => api.getClanLeaderboardSinceReset(referralCode),
    { refreshInterval: 60_000, revalidateOnFocus: false }
  );

  const handleConfirm = async () => {
    if (!isBlinkoLoginTokenValid(ownerAddress)) {
      toast.error("Please sign in to reset your leaderboard");
      setWalletModalVisible(true);
      return;
    }
    try {
      const res = await api.resetReferralLeaderboard(ownerAddress);
      await mutateSinceReset();
      toast.success(
        `Season started at ${formatSeasonStart(res.resetAt)}`
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to reset leaderboard");
    }
  };

  return (
    <>
      <div className="mt-4">
        <InnerShape2 cornerDiagonal={10} cornerRadius={8} strokeWidth={1}>
          <div className="flex flex-col md:flex-row md:items-center gap-3 p-3 md:p-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <InnerShape cornerDiagonal={6} cornerRadius={4} strokeWidth={1}>
                  <div className="p-1.5">
                    <img src={icons.cup} className="w-3.5 h-3.5" alt="Season" />
                  </div>
                </InnerShape>
                <p className="text-[10px] uppercase font-blinko text-white/50 tracking-wider">
                  Clan Leaderboard Season
                </p>
              </div>
              <p className="mt-2 text-sm text-white">
                {sinceReset?.since
                  ? `Started ${formatSeasonStart(sinceReset.since)}`
                  : "No season started yet"}
              </p>
              <p className="text-xs text-white/50 mt-1">
                Rankings count Honey earned since this point. Resetting starts a
                fresh season and can't be undone.
              </p>
            </div>
            <button
              onClick={() => setShowConfirm(true)}
              className="shrink-0 self-start md:self-center"
            >
              <InnerShape cornerDiagonal={12} cornerRadius={8} strokeWidth={1}>
                <div className="px-5 py-2.5">
                  <p className="text-sm font-blinko text-white whitespace-nowrap">
                    Start new season
                  </p>
                </div>
              </InnerShape>
            </button>
          </div>
        </InnerShape2>
      </div>

      {showConfirm && (
        <ResetLeaderboardModal
          onDismiss={() => setShowConfirm(false)}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
};

export default function KolPage() {
  const { address } = useParams();
  const { data: profile, isLoading } = useKolProfile(address || "");
  const { address: connectedAddress } = useAccount();

  if (!address) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <p className="text-red-400">Invalid address</p>
      </div>
    );
  }

  const isOwner =
    isAddress(address) &&
    !!connectedAddress &&
    isAddressEqual(address as `0x${string}`, connectedAddress);

  return (
    <div className="min-h-screen bg-black text-white overflow-y-auto">
      <div className="relative z-10 w-full max-w-5xl mx-auto px-2 py-4">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <Loader size="48px" stroke="#ffffff" />
          </div>
        ) : profile ? (
          <ContainerShape
            cornerDiagonal={20}
            cornerRadius={12}
            strokeWidth={1}
          >
            <div className="p-3 md:p-6 backdrop-blur-sm">
              {/* Header - Profile Info */}
              <div className="flex items-center gap-4 mb-6">
                <InnerShape cornerDiagonal={12} cornerRadius={8} strokeWidth={1}>
                  <div className="p-2">
                    {profile.picture ? (
                      <img
                        src={profile.picture}
                        alt={profile.name}
                        className="w-16 h-16 rounded-lg object-cover"
                      />
                    ) : (
                      <img
                        src={icons.profilePlaceholder}
                        alt="Profile"
                        className="w-16 h-16"
                      />
                    )}
                  </div>
                </InnerShape>
                <div className="flex flex-col gap-1">
                  <h1 className="text-2xl font-black text-white">
                    {profile.name}
                  </h1>
                  {profile.twitterHandle && (
                    <a
                      href={`https://twitter.com/${profile.twitterHandle}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1 text-blue-400 hover:underline text-sm"
                    >
                      <img src={icons.twitterX} className="w-3.5 h-3.5" alt="Twitter" />
                      @{profile.twitterHandle}
                    </a>
                  )}
                  <p className="text-xs text-white/50 font-mono">
                    {profile.address.slice(0, 6)}...{profile.address.slice(-4)}
                  </p>
                </div>
                {profile.referralCode && (
                  <div className="ml-auto">
                    <InnerShape cornerDiagonal={8} cornerRadius={6} strokeWidth={1}>
                      <div className="px-4 py-2 text-center">
                        <p className="text-[10px] text-white/50 uppercase tracking-wider">
                          Referral Code
                        </p>
                        <p className="text-xl font-black text-yellow-400">
                          {profile.referralCode}
                        </p>
                      </div>
                    </InnerShape>
                  </div>
                )}
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <StatCard
                  title="Profit Share"
                  value={
                    <span>
                      {formatEth(BigInt(profile.pendingProfitShare) + BigInt(profile.settledProfitShare))}{" "}
                      <span className="text-sm text-white/50">ETH</span>
                    </span>
                  }
                  subValue={`Settled: ${formatEth(BigInt(profile.settledProfitShare))} ETH | ${(profile.profitShareBps / 100).toFixed(1)}% rate`}
                  icon={icons.cashBanknote}
                  colorClass="text-green-400"
                />

                <StatCard
                  title="Total Honey"
                  value={formatHoney(profile.totalHoney)}
                  subValue={`Referral: ${formatHoney(profile.referralHoney)} | Today: ${formatHoney(profile.stats.todayTotalHoney)}`}
                  icon={icons.coins}
                  colorClass="text-yellow-400"
                />

                <StatCard
                  title="Referrals"
                  value={profile.stats.referralsCount.toLocaleString()}
                  icon={icons.userPlus}
                />
              </div>

              {/* Owner-only: Clan leaderboard reset */}
              {isOwner && profile.referralCode && (
                <LeaderboardResetSection
                  ownerAddress={profile.address as `0x${string}`}
                  referralCode={profile.referralCode}
                />
              )}

              {/* Member Info */}
              <div className="mt-4 flex justify-between items-center text-xs text-white/40 px-2">
                <span>
                  Member since:{" "}
                  {new Date(profile.createdAt * 1000).toLocaleDateString()}
                </span>
                {profile.usedReferralCode && (
                  <span>Referred by code: {profile.usedReferralCode}</span>
                )}
              </div>
            </div>
          </ContainerShape>
        ) : (
          <div className="flex justify-center items-center h-64">
            <p className="text-red-400">Failed to load profile data.</p>
          </div>
        )}
      </div>
    </div>
  );
}
